import { ReflectUtils } from "@dooboostore/core";
import { ensureInit, getElementConfig } from "./elementDefine.js";
import { SwcUtils } from "../utils/Utils.js";
const PROPERTY_METADATA_KEY = Symbol.for("simple-web-component:property");
const resolvePropertyTargets = (inst, selector, options) => {
  const conf = getElementConfig(inst);
  const currentWin = conf.window;
  const r = options.root || "auto";
  const results = [];
  let resolvedSelector = selector;
  if (typeof selector === "function") {
    const hostSet = SwcUtils.getHelperAndHostSet(inst);
    resolvedSelector = selector(inst, hostSet);
  }
  if (resolvedSelector instanceof currentWin.Element) {
    results.push(resolvedSelector);
    return results;
  }
  if (resolvedSelector instanceof currentWin.NodeList) {
    results.push(...Array.from(resolvedSelector));
    return results;
  }
  if (Array.isArray(resolvedSelector)) {
    results.push(...resolvedSelector);
    return results;
  }
  if (resolvedSelector === null) {
    return results;
  }
  const stringSelector = resolvedSelector;
  const applyRoot = (target) => {
    if (!target) return;
    if (r === "auto") {
      results.push(target);
    } else {
      if (r === "light" || r === "all") results.push(target);
      if ((r === "shadow" || r === "all") && target.shadowRoot) results.push(target.shadowRoot);
    }
  };
  if (stringSelector === "$this" || !stringSelector) {
    applyRoot(inst);
  } else if (stringSelector === "$window") {
    results.push(currentWin);
  } else if (stringSelector === "$document") {
    results.push(currentWin.document);
  } else {
    const hostSet = SwcUtils.getHostSet(inst);
    if (stringSelector === "$host") applyRoot(hostSet.$host);
    else if (stringSelector === "$parentHost") applyRoot(hostSet.$parentHost);
    else if (stringSelector === "$appHost") applyRoot(hostSet.$appHost);
    else if (stringSelector === "$firstHost") applyRoot(hostSet.$firstHost);
    else if (stringSelector === "$lastHost") applyRoot(hostSet.$lastHost);
    else if (stringSelector === "$firstAppHost") applyRoot(hostSet.$firstAppHost);
    else if (stringSelector === "$lastAppHost") applyRoot(hostSet.$lastAppHost);
    else if (stringSelector === "$hosts") hostSet.$hosts.forEach(applyRoot);
    else if (stringSelector === "$appHosts") hostSet.$appHosts.forEach(applyRoot);
    else {
      if (r === "shadow") {
        const found = inst.shadowRoot?.querySelectorAll(stringSelector);
        if (found) results.push(...found);
      } else if (r === "light") {
        const found = inst.querySelectorAll(stringSelector);
        if (found) results.push(...found);
      } else if (r === "all") {
        const sMatch = inst.shadowRoot?.querySelectorAll(stringSelector);
        const lMatch = inst.querySelectorAll(stringSelector);
        if (sMatch) results.push(...sMatch);
        if (lMatch) results.push(...lMatch);
      } else {
        const found = (inst.shadowRoot || inst).querySelectorAll(stringSelector);
        if (found) results.push(...found);
      }
    }
  }
  return results;
};
function applyProperty(selector, targetPropertyKeyOrOptions, options) {
  return (target, propertyKey, descriptor) => {
    const constructor = target.constructor;
    const metaType = descriptor && typeof descriptor.value === "function" ? "method" : "property";
    let finalOptions = {};
    let targetPropertyKey;
    if (typeof targetPropertyKeyOrOptions === "string" || typeof targetPropertyKeyOrOptions === "symbol") {
      targetPropertyKey = targetPropertyKeyOrOptions;
      finalOptions = options || {};
    } else if (targetPropertyKeyOrOptions) {
      finalOptions = targetPropertyKeyOrOptions;
      targetPropertyKey = finalOptions.name || propertyKey;
    } else {
      targetPropertyKey = propertyKey;
    }
    let metaList = ReflectUtils.getOwnMetadata(PROPERTY_METADATA_KEY, constructor);
    if (!metaList) {
      metaList = [];
      ReflectUtils.defineMetadata(PROPERTY_METADATA_KEY, metaList, constructor);
    }
    metaList.push({
      propertyKey,
      selector,
      targetPropertyKey,
      options: finalOptions,
      type: metaType
    });
    const applyValueToTargets = (inst, resolvedValue) => {
      if (resolvedValue === void 0) return resolvedValue;
      const targetEls = resolvePropertyTargets(inst, selector, finalOptions);
      const hostSet = SwcUtils.getHelperAndHostSet(inst);
      targetEls.forEach((targetEl) => {
        if (finalOptions.filter && !finalOptions.filter(targetEl, resolvedValue, { currentThis: inst, helper: hostSet })) {
          return;
        }
        const resolvedRes = typeof resolvedValue === "function" ? resolvedValue(targetEl, hostSet) : resolvedValue;
        if (finalOptions.setType === "call") {
          if (typeof targetEl[targetPropertyKey] !== "function") return;
          if (Array.isArray(resolvedRes)) targetEl[targetPropertyKey].apply(targetEl, resolvedRes);
          else targetEl[targetPropertyKey].call(targetEl, resolvedRes);
          return;
        }
        targetEl[targetPropertyKey] = resolvedRes;
      });
      return resolvedValue;
    };
    if (descriptor && typeof descriptor.value === "function") {
      const original = descriptor.value;
      descriptor.value = function(...args) {
        ensureInit(this);
        const res = original.apply(this, args);
        const extractValue = (v) => {
          if (v && typeof v === "object" && PROPERTY_METADATA_KEY in v) {
            return v[PROPERTY_METADATA_KEY];
          }
          return v;
        };
        if (res instanceof Promise) {
          return res.then((v) => {
            const extracted = extractValue(v);
            applyValueToTargets(this, extracted);
            return v;
          });
        } else {
          const extracted = extractValue(res);
          applyValueToTargets(this, extracted);
          return res;
        }
      };
      return;
    }
    if (selector === "$this" && targetPropertyKey === propertyKey) {
      return;
    }
    Object.defineProperty(target, propertyKey, {
      configurable: true,
      enumerable: true,
      get() {
        ensureInit(this);
        const targetEls = resolvePropertyTargets(this, selector, finalOptions);
        const hostSet = SwcUtils.getHelperAndHostSet(this);
        const filtered = targetEls.filter((el) => {
          if (finalOptions.filter && !finalOptions.filter(el, el[targetPropertyKey], { currentThis: this, helper: hostSet })) {
            return false;
          }
          return true;
        });
        const values = filtered.map((el) => {
          const raw = el[targetPropertyKey];
          if (finalOptions.getType === "call" && typeof raw === "function") return raw.call(el);
          return raw;
        });
        return values.length === 1 ? values[0] : values;
      },
      set(value) {
        ensureInit(this);
        applyValueToTargets(this, value);
      }
    });
  };
}
function property(selectorOrTarget, targetPropertyKeyOrOptions, optionsOrDescriptor) {
  if (typeof selectorOrTarget === "string" || typeof selectorOrTarget === "function") {
    return applyProperty(selectorOrTarget, targetPropertyKeyOrOptions, optionsOrDescriptor);
  }
  if (selectorOrTarget && (typeof targetPropertyKeyOrOptions === "string" || typeof targetPropertyKeyOrOptions === "symbol")) {
    return applyProperty("$this", {})(selectorOrTarget, targetPropertyKeyOrOptions, optionsOrDescriptor);
  }
  return applyProperty("$this", selectorOrTarget);
}
function callProperty(selectorOrTarget, targetMethodKeyOrOptions, optionsOrUndefined) {
  if (typeof selectorOrTarget === "string" || typeof selectorOrTarget === "function") {
    if (typeof targetMethodKeyOrOptions === "string" || typeof targetMethodKeyOrOptions === "symbol") {
      return applyProperty(selectorOrTarget, targetMethodKeyOrOptions, { ...optionsOrUndefined ?? {}, setType: "call" });
    }
    return applyProperty(selectorOrTarget, { ...optionsOrUndefined ?? targetMethodKeyOrOptions ?? {}, setType: "call" });
  }
  return applyProperty("$this", void 0, { ...selectorOrTarget ?? {}, setType: "call" });
}
function callPropertyLight(selector, targetMethodKeyOrOptions, options) {
  if (targetMethodKeyOrOptions != null && typeof targetMethodKeyOrOptions !== "string" && typeof targetMethodKeyOrOptions !== "symbol") {
    return callProperty(selector, void 0, { ...targetMethodKeyOrOptions ?? {}, root: "light" });
  }
  return callProperty(selector, targetMethodKeyOrOptions, { ...options ?? {}, root: "light" });
}
function callPropertyShadow(selector, targetMethodKeyOrOptions, options) {
  if (targetMethodKeyOrOptions != null && typeof targetMethodKeyOrOptions !== "string" && typeof targetMethodKeyOrOptions !== "symbol") {
    return callProperty(selector, void 0, { ...targetMethodKeyOrOptions ?? {}, root: "shadow" });
  }
  return callProperty(selector, targetMethodKeyOrOptions, { ...options ?? {}, root: "shadow" });
}
function callPropertyAll(selector, targetMethodKey, options) {
  return callProperty(selector, targetMethodKey, { ...options ?? {}, root: "all" });
}
const findAllPropertyMetadata = (target) => {
  const actualTarget = typeof target === "function" ? target : target.constructor;
  return ReflectUtils.findAllMetadata(PROPERTY_METADATA_KEY, actualTarget).flat();
};
const findAllPropertyApplyMetadata = (target) => {
  const result = /* @__PURE__ */ new Map();
  findAllPropertyMetadata(target).forEach((meta) => {
    result.set(meta.propertyKey, meta);
  });
  return result;
};
const prop = applyProperty;
const dispatchPropConvenience = (selector, targetPropertyKeyOrTarget, optionsOrPropertyKey, descriptor) => {
  if ((typeof optionsOrPropertyKey === "string" || typeof optionsOrPropertyKey === "symbol") && descriptor !== void 0) {
    return applyProperty(selector, void 0, {})(targetPropertyKeyOrTarget, optionsOrPropertyKey, descriptor);
  }
  return applyProperty(selector, targetPropertyKeyOrTarget, optionsOrPropertyKey);
};
function propAppHost(targetPropertyKeyOrTarget, optionsOrPropertyKey, descriptor) {
  return dispatchPropConvenience("$appHost", targetPropertyKeyOrTarget, optionsOrPropertyKey, descriptor);
}
function propWindow(targetPropertyKeyOrTarget, optionsOrPropertyKey, descriptor) {
  return dispatchPropConvenience("$window", targetPropertyKeyOrTarget, optionsOrPropertyKey, descriptor);
}
function propDocument(targetPropertyKeyOrTarget, optionsOrPropertyKey, descriptor) {
  return dispatchPropConvenience("$document", targetPropertyKeyOrTarget, optionsOrPropertyKey, descriptor);
}
function propLight(selector, targetPropertyKey, options) {
  return applyProperty(selector, targetPropertyKey, { ...options ?? {}, root: "light" });
}
function propShadow(selector, targetPropertyKey, options) {
  return applyProperty(selector, targetPropertyKey, { ...options ?? {}, root: "shadow" });
}
function propAll(selector, targetPropertyKey, options) {
  return applyProperty(selector, targetPropertyKey, { ...options ?? {}, root: "all" });
}
function propertyLight(selector, targetPropertyKey, options) {
  return applyProperty(selector, targetPropertyKey, { ...options ?? {}, root: "light" });
}
function propertyShadow(selector, targetPropertyKey, options) {
  return applyProperty(selector, targetPropertyKey, { ...options ?? {}, root: "shadow" });
}
function propertyAll(selector, targetPropertyKey, options) {
  return applyProperty(selector, targetPropertyKey, { ...options ?? {}, root: "all" });
}
var applyProperty_default = applyProperty;
export {
  PROPERTY_METADATA_KEY,
  applyProperty,
  callProperty,
  callPropertyAll,
  callPropertyLight,
  callPropertyShadow,
  applyProperty_default as default,
  findAllPropertyApplyMetadata,
  findAllPropertyMetadata,
  prop,
  propAll,
  propAppHost,
  propDocument,
  propLight,
  propShadow,
  propWindow,
  property,
  propertyAll,
  propertyLight,
  propertyShadow,
  resolvePropertyTargets
};
//# sourceMappingURL=applyProperty.js.map
